import { useMemo } from 'react';
import { SwitchAccount } from '@mui/icons-material';
import { Button, Typography } from '@mui/material';
import { DataGridProps, GridColDef, renderBooleanCell } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import { AdminUsersQuery, Role } from '../../../../api/gql';
import { renderCellOrDefault } from '../../../../components/table/renderCellOrDefault';
import { Table } from '../../../../components/table/Table';
import { useImpersonate } from '../../../../hooks/useImpersonate';

type Props = {
  users?: AdminUsersQuery['adminUsers']['results'];
} & Partial<DataGridProps>;

export const AdminUsersTable = ({ users, ...rest }: Props) => {
  const { t } = useTranslation('page-admin-users');
  const { impersonateUser } = useImpersonate();

  const rows = useMemo(
    () =>
      users?.map((user) => ({
        ...user,
        isAdmin: user.role === Role.Admin,
      })) ?? [],
    [users],
  );

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'lastName',
        headerName: t('table.lastName') as string,
        flex: 1,
        minWidth: 100,
        renderCell: renderCellOrDefault,
      },
      {
        field: 'firstName',
        headerName: t('table.firstName') as string,
        flex: 1,
        minWidth: 100,
        renderCell: renderCellOrDefault,
      },
      {
        field: 'email',
        headerName: t('table.email') as string,
        flex: 1,
        minWidth: 200,
        renderCell: renderCellOrDefault,
      },
      {
        field: 'phoneNumber',
        headerName: t('table.phoneNumber') as string,
        flex: 1,
        minWidth: 100,
        renderCell: renderCellOrDefault,
      },
      {
        field: 'vatId',
        headerName: t('table.vat') as string,
        flex: 1,
        minWidth: 100,
        renderCell: renderCellOrDefault,
      },
      {
        field: 'businessName',
        headerName: t('table.businessName') as string,
        flex: 1,
        minWidth: 150,
        renderCell: renderCellOrDefault,
      },
      {
        field: 'streetAddress',
        headerName: t('table.streetAddress') as string,
        flex: 1,
        minWidth: 150,
        renderCell: renderCellOrDefault,
      },
      {
        field: 'city',
        headerName: t('table.city') as string,
        flex: 1,
        minWidth: 150,
        renderCell: renderCellOrDefault,
      },
      {
        field: 'zipCode',
        headerName: t('table.zipCode') as string,
        flex: 1,
        minWidth: 100,
        renderCell: renderCellOrDefault,
      },
      {
        field: 'country',
        headerName: t('table.country') as string,
        flex: 1,
        minWidth: 150,
        renderCell: renderCellOrDefault,
      },
      {
        field: 'isAdmin',
        headerName: t('table.isAdmin') as string,
        type: 'boolean',
        flex: 1,
        minWidth: 110,
        renderCell: renderBooleanCell,
      },
      {
        field: 'isEnterprise',
        headerName: t('table.isEnterprise') as string,
        type: 'boolean',
        flex: 1,
        minWidth: 80,
        renderCell: renderBooleanCell,
      },
      {
        field: 'id',
        headerName: '',
        type: 'actions',
        flex: 1,
        minWidth: 220,
        renderCell: (params) => (
          <Button startIcon={<SwitchAccount />} onClick={() => impersonateUser(params.value)}>
            {t('impersonate')}
          </Button>
        ),
      },
    ],
    [impersonateUser, t],
  );

  return (
    <>
      <Table
        id={'admin-users'}
        {...rest}
        columns={columns}
        rows={rows}
        experimentalFeatures={{ columnGrouping: true }}
        columnGroupingModel={[
          {
            groupId: 'contactInfo',
            headerName: t('table.contactInfo') as string,
            renderHeaderGroup: (params) => {
              return (
                <Typography variant={'body2'} color={'textSecondary'}>
                  {params.headerName}
                </Typography>
              );
            },
            children: [{ field: 'firstName' }, { field: 'lastName' }, { field: 'email' }, { field: 'phoneNumber' }],
          },
          {
            groupId: 'billingInfo',
            headerName: t('table.billingInfo') as string,
            renderHeaderGroup: (params) => {
              return (
                <Typography variant={'body2'} color={'textSecondary'}>
                  {params.headerName}
                </Typography>
              );
            },
            children: [
              { field: 'vatId' },
              { field: 'businessName' },
              { field: 'streetAddress' },
              { field: 'city' },
              { field: 'zipCode' },
              { field: 'country' },
            ],
          },
          {
            groupId: 'systemInfo',
            headerName: t('table.systemInfo') as string,
            renderHeaderGroup: (params) => {
              return (
                <Typography variant={'body2'} color={'textSecondary'}>
                  {params.headerName}
                </Typography>
              );
            },
            children: [{ field: 'isAdmin' }, { field: 'isEnterprise' }],
          },
        ]}
      />
    </>
  );
};
